import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';

interface BoxListing {
  id: string;
  title: string;
  brand: string;
  boxType: string;
  year: number;
  sport: 'basketball' | 'football' | 'baseball' | 'hockey' | 'soccer';
  price: number;
  originalPrice?: number;
  description: string;
  imageUrl: string;
  additionalImageUrls?: string[];
  isPosted: boolean;
  isHomepage: boolean;
  condition: string;
  quantity: number;
  createdAt: string;
  updatedAt: string;
}

const AdminBoxesManager: React.FC = () => {
  const { mode } = useTheme();
  const [boxes, setBoxes] = useState<BoxListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingBox, setEditingBox] = useState<BoxListing | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [boxToDelete, setBoxToDelete] = useState<BoxListing | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [newBox, setNewBox] = useState<Partial<BoxListing>>({
    title: '',
    brand: '',
    boxType: '',
    year: new Date().getFullYear(),
    sport: 'basketball',
    price: 0,
    description: '',
    imageUrl: '',
    additionalImageUrls: [],
    isPosted: false,
    isHomepage: false,
    condition: 'New',
    quantity: 1,
  });

  // Predefined options for dropdowns
  const brandOptions = [
    'Panini',
    'Topps',
    'Upper Deck',
    'Fleer',
    'Donruss',
    'Bowman',
    'Leaf',
    'Pinnacle',
    'Score',
    'Skybox',
    'Stadium Club',
    'Finest',
    'Chrome',
    'Prizm',
    'Select',
    'Optic',
    'Contenders',
    'Immaculate',
    'National Treasures',
    'Flawless',
    'Other'
  ];

  const boxTypeOptions = [
    'Hobby Box',
    'Retail Box',
    'Blaster Box',
    'Fat Pack',
    'Hanger Box',
    'Jumbo Box',
    'Super Jumbo Box',
    'Case',
    'Half Case',
    'Wax Box',
    'Vintage Box',
    'Other'
  ];

  const yearOptions = Array.from({ length: 35 }, (_, i) => new Date().getFullYear() - i).reverse();

  // Load boxes from localStorage on component mount
  useEffect(() => {
    loadBoxes();
  }, []);

  const loadBoxes = () => {
    try {
      setLoading(true);
      const savedBoxes = localStorage.getItem('adminBoxes');
      if (savedBoxes) {
        setBoxes(JSON.parse(savedBoxes));
      } else {
        setBoxes([]);
      }
      setError(null);
    } catch (err) {
      setError('Failed to load boxes');
      setBoxes([]);
    } finally {
      setLoading(false);
    }
  };

  const saveBoxes = (updatedBoxes: BoxListing[]) => {
    try {
      localStorage.setItem('adminBoxes', JSON.stringify(updatedBoxes));
      setBoxes(updatedBoxes);
      setSnackbar({ open: true, message: 'Boxes saved successfully!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to save boxes', severity: 'error' });
    }
  };

  const handleAddBox = () => {
    if (!newBox.title || !newBox.brand || !newBox.price) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }

    const box: BoxListing = {
      id: Date.now().toString(),
      title: newBox.title!,
      brand: newBox.brand!,
      boxType: newBox.boxType!,
      year: newBox.year!,
      sport: newBox.sport!,
      price: newBox.price!,
      originalPrice: newBox.originalPrice,
      description: newBox.description!,
      imageUrl: newBox.imageUrl!,
      additionalImageUrls: newBox.additionalImageUrls || [],
      isPosted: newBox.isPosted!,
      isHomepage: newBox.isHomepage!,
      condition: newBox.condition!,
      quantity: newBox.quantity!,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    const updatedBoxes = [...boxes, box];
    saveBoxes(updatedBoxes);
    setIsAddDialogOpen(false);
    resetNewBox();
  };

  const handleEditBox = (box: BoxListing) => {
    setEditingBox(box);
  };

  const handleSaveEdit = () => {
    if (!editingBox) return;

    const updatedBoxes = boxes.map(box => 
      box.id === editingBox.id ? { ...editingBox, updatedAt: new Date().toISOString() } : box
    );
    saveBoxes(updatedBoxes);
    setEditingBox(null);
  };

  const handleDeleteBox = (box: BoxListing) => {
    setBoxToDelete(box);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!boxToDelete) return;

    const updatedBoxes = boxes.filter(box => box.id !== boxToDelete.id);
    saveBoxes(updatedBoxes);
    setIsDeleteDialogOpen(false);
    setBoxToDelete(null);
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to delete all boxes? This action cannot be undone.')) {
      saveBoxes([]);
    }
  };

  const resetNewBox = () => {
    setNewBox({
      title: '',
      brand: '',
      boxType: '',
      year: new Date().getFullYear(),
      sport: 'basketball',
      price: 0,
      description: '',
      imageUrl: '',
      additionalImageUrls: [],
      isPosted: false,
      isHomepage: false,
      condition: 'New',
      quantity: 1,
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(price);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box sx={{ mb: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            Buy Boxes Manager
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your sports card box listings
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleClearAll}
            startIcon={<ClearIcon />}
          >
            Clear All Boxes
          </Button>
          <Button
            variant="contained"
            onClick={() => setIsAddDialogOpen(true)}
            startIcon={<AddIcon />}
          >
            Add Box
          </Button>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* Boxes Grid */}
      {boxes.length > 0 ? (
        <Grid container spacing={3}>
          {boxes.map((box) => (
            <Grid item xs={12} sm={6} md={4} key={box.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <CardMedia
                          component="img"
                          height="200"
                          image={box.imageUrl || '/placeholder-box.jpg'}
                          alt={box.title}
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).src = '/placeholder-box.jpg';
                          }}
                          sx={{ 
                            objectFit: 'contain',
                            backgroundColor: mode === 'light' ? '#f8f9fa' : '#2d3748',
                            padding: '8px'
                          }}
                        />
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {box.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {box.brand} • {box.boxType} • {box.year} • {box.sport}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}>
                    {formatPrice(box.price)}
                  </Typography>
                  
                                            <Box sx={{ mb: 2 }}>
                            <Chip 
                              label={box.condition} 
                              size="small" 
                              sx={{ mr: 1, mb: 1 }}
                            />
                            {box.isPosted && (
                              <Chip 
                                label="Posted" 
                                size="small" 
                                color="success" 
                                sx={{ mr: 1, mb: 1 }}
                              />
                            )}
                            {box.isHomepage && (
                              <Chip 
                                label="Homepage" 
                                size="small" 
                                color="primary" 
                                sx={{ mr: 1, mb: 1 }}
                              />
                            )}
                            {box.additionalImageUrls && box.additionalImageUrls.length > 0 && (
                              <Chip 
                                label={`${box.additionalImageUrls.length} additional images`}
                                size="small" 
                                color="info" 
                                sx={{ mr: 1, mb: 1 }}
                              />
                            )}
                          </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                    {box.description.length > 100 
                      ? `${box.description.substring(0, 100)}...` 
                      : box.description
                    }
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditBox(box)}
                      fullWidth
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteBox(box)}
                    >
                      Delete
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            No boxes found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Get started by adding your first sports card box listing.
          </Typography>
          <Button
            variant="contained"
            onClick={() => setIsAddDialogOpen(true)}
            startIcon={<AddIcon />}
          >
            Add Your First Box
          </Button>
        </Paper>
      )}

      {/* Add Box Dialog */}
      <Dialog 
        open={isAddDialogOpen} 
        onClose={() => setIsAddDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Box</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Box Title"
                value={newBox.title}
                onChange={(e) => setNewBox({ ...newBox, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Brand</InputLabel>
                <Select
                  value={newBox.brand}
                  label="Brand"
                  onChange={(e) => setNewBox({ ...newBox, brand: e.target.value })}
                >
                  {brandOptions.map((brand) => (
                    <MenuItem key={brand} value={brand}>
                      {brand}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Box Type</InputLabel>
                <Select
                  value={newBox.boxType}
                  label="Box Type"
                  onChange={(e) => setNewBox({ ...newBox, boxType: e.target.value })}
                >
                  {boxTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Year</InputLabel>
                <Select
                  value={newBox.year?.toString() || ''}
                  label="Year"
                  onChange={(e) => setNewBox({ ...newBox, year: parseInt(e.target.value) })}
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Sport</InputLabel>
                <Select
                  value={newBox.sport}
                  label="Sport"
                  onChange={(e) => setNewBox({ ...newBox, sport: e.target.value as any })}
                >
                  <MenuItem value="basketball">Basketball</MenuItem>
                  <MenuItem value="football">Football</MenuItem>
                  <MenuItem value="baseball">Baseball</MenuItem>
                  <MenuItem value="hockey">Hockey</MenuItem>
                  <MenuItem value="soccer">Soccer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price"
                type="number"
                value={newBox.price}
                onChange={(e) => setNewBox({ ...newBox, price: parseFloat(e.target.value) })}
                required
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={newBox.quantity}
                onChange={(e) => setNewBox({ ...newBox, quantity: parseInt(e.target.value) })}
                required
              />
            </Grid>
                                <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Primary Image URL"
                        value={newBox.imageUrl}
                        onChange={(e) => setNewBox({ ...newBox, imageUrl: e.target.value })}
                        placeholder="https://example.com/image.jpg"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Additional Image URLs
                      </Typography>
                      {newBox.additionalImageUrls?.map((url, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <TextField
                            fullWidth
                            label={`Additional Image ${index + 1}`}
                            value={url}
                            onChange={(e) => {
                              const updatedUrls = [...(newBox.additionalImageUrls || [])];
                              updatedUrls[index] = e.target.value;
                              setNewBox({ ...newBox, additionalImageUrls: updatedUrls });
                            }}
                            placeholder="https://example.com/additional-image.jpg"
                          />
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              const updatedUrls = newBox.additionalImageUrls?.filter((_, i) => i !== index) || [];
                              setNewBox({ ...newBox, additionalImageUrls: updatedUrls });
                            }}
                            sx={{ minWidth: 'auto', px: 2 }}
                          >
                            Remove
                          </Button>
                        </Box>
                      ))}
                      <Button
                        variant="outlined"
                        onClick={() => {
                          const currentUrls = newBox.additionalImageUrls || [];
                          setNewBox({ 
                            ...newBox, 
                            additionalImageUrls: [...currentUrls, ''] 
                          });
                        }}
                        startIcon={<AddIcon />}
                        sx={{ mt: 1 }}
                      >
                        Add Another Image URL
                      </Button>
                    </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={newBox.description}
                onChange={(e) => setNewBox({ ...newBox, description: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newBox.isPosted}
                    onChange={(e) => setNewBox({ ...newBox, isPosted: e.target.checked })}
                  />
                }
                label="Post to Buy Boxes Page"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newBox.isHomepage}
                    onChange={(e) => setNewBox({ ...newBox, isHomepage: e.target.checked })}
                  />
                }
                label="Show on Homepage"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddBox} variant="contained">Add Box</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Box Dialog */}
      {editingBox && (
        <Dialog 
          open={!!editingBox} 
          onClose={() => setEditingBox(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit Box</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Box Title"
                  value={editingBox.title}
                  onChange={(e) => setEditingBox({ ...editingBox, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Brand</InputLabel>
                  <Select
                    value={editingBox.brand}
                    label="Brand"
                    onChange={(e) => setEditingBox({ ...editingBox, brand: e.target.value })}
                  >
                    {brandOptions.map((brand) => (
                      <MenuItem key={brand} value={brand}>
                        {brand}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Box Type</InputLabel>
                  <Select
                    value={editingBox.boxType}
                    label="Box Type"
                    onChange={(e) => setEditingBox({ ...editingBox, boxType: e.target.value })}
                  >
                    {boxTypeOptions.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Year</InputLabel>
                  <Select
                    value={editingBox.year.toString()}
                    label="Year"
                    onChange={(e) => setEditingBox({ ...editingBox, year: parseInt(e.target.value) })}
                  >
                    {yearOptions.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Sport</InputLabel>
                  <Select
                    value={editingBox.sport}
                    label="Sport"
                    onChange={(e) => setEditingBox({ ...editingBox, sport: e.target.value as any })}
                  >
                    <MenuItem value="basketball">Basketball</MenuItem>
                    <MenuItem value="football">Football</MenuItem>
                    <MenuItem value="baseball">Baseball</MenuItem>
                    <MenuItem value="hockey">Hockey</MenuItem>
                    <MenuItem value="soccer">Soccer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  value={editingBox.price}
                  onChange={(e) => setEditingBox({ ...editingBox, price: parseFloat(e.target.value) })}
                  required
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quantity"
                  type="number"
                  value={editingBox.quantity}
                  onChange={(e) => setEditingBox({ ...editingBox, quantity: parseInt(e.target.value) })}
                  required
                />
              </Grid>
                                    <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Primary Image URL"
                          value={editingBox.imageUrl}
                          onChange={(e) => setEditingBox({ ...editingBox, imageUrl: e.target.value })}
                          placeholder="https://example.com/image.jpg"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                          Additional Image URLs
                        </Typography>
                        {editingBox.additionalImageUrls?.map((url, index) => (
                          <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <TextField
                              fullWidth
                              label={`Additional Image ${index + 1}`}
                              value={url}
                              onChange={(e) => {
                                const updatedUrls = [...(editingBox.additionalImageUrls || [])];
                                updatedUrls[index] = e.target.value;
                                setEditingBox({ ...editingBox, additionalImageUrls: updatedUrls });
                              }}
                              placeholder="https://example.com/additional-image.jpg"
                            />
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={() => {
                                const updatedUrls = editingBox.additionalImageUrls?.filter((_, i) => i !== index) || [];
                                setEditingBox({ ...editingBox, additionalImageUrls: updatedUrls });
                              }}
                              sx={{ minWidth: 'auto', px: 2 }}
                            >
                              Remove
                            </Button>
                          </Box>
                        ))}
                        <Button
                          variant="outlined"
                          onClick={() => {
                            const currentUrls = editingBox.additionalImageUrls || [];
                            setEditingBox({ 
                              ...editingBox, 
                              additionalImageUrls: [...currentUrls, ''] 
                            });
                          }}
                          startIcon={<AddIcon />}
                          sx={{ mt: 1 }}
                        >
                          Add Another Image URL
                        </Button>
                      </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={4}
                  value={editingBox.description}
                  onChange={(e) => setEditingBox({ ...editingBox, description: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingBox.isPosted}
                      onChange={(e) => setEditingBox({ ...editingBox, isPosted: e.target.checked })}
                    />
                  }
                  label="Post to Buy Boxes Page"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingBox.isHomepage}
                      onChange={(e) => setEditingBox({ ...editingBox, isHomepage: e.target.checked })}
                    />
                  }
                  label="Show on Homepage"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditingBox(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} variant="contained">Save Changes</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{boxToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminBoxesManager; 